import { IconButton } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { defined } from '@vy/null';
import { useCallback, useState } from 'react';
import {
  type FilterField,
  type FilterLineType,
} from '../../../../components/new-design/vyTableFilter';
import { VyTableColumnSettings } from '../../../../components/new-design/table/table-column-settings/VyTableColumnSettings';
import type { ColumnConfig } from '../../../../hooks/use-grid-column-settings';
import { reportIconButtonSx } from '../../reports-components/report-toolbar-styles';
import { RevenueReportToolbar } from '../../reports-components/RevenueReportToolbar';

type CustomerMasterReportToolbarProps = {
  onSearchChange: (value: string) => void;
  searchValue?: string;
  filterFields: FilterField[];
  filters: FilterLineType[];
  onFiltersChange: (filters: FilterLineType[]) => void;
  columnSettings: ColumnConfig[];
  onColumnSettingsOpen: () => void;
  onColumnSettingsClose: () => void;
  onColumnVisibilityChange: (field: string, isVisible: boolean) => void;
  onExport: () => void;
};

export const CustomerMasterReportToolbar: React.FC<CustomerMasterReportToolbarProps> = ({
  onSearchChange,
  searchValue,
  filterFields,
  filters,
  onFiltersChange,
  columnSettings,
  onColumnSettingsOpen,
  onColumnSettingsClose,
  onColumnVisibilityChange,
  onExport,
}) => {
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const isColumnSettingsOpen = defined(columnSettingsAnchorEl);

  const handleOpenColumnSettings = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setColumnSettingsAnchorEl(event.currentTarget);
      onColumnSettingsOpen();
    },
    [onColumnSettingsOpen],
  );

  const handleCloseColumnSettings = useCallback(() => {
    setColumnSettingsAnchorEl(null);
    onColumnSettingsClose();
  }, [onColumnSettingsClose]);

  const trailingActions = (
    <>
      <IconButton
        onClick={handleOpenColumnSettings}
        data-vy="customer-master-report-column-settings"
        sx={reportIconButtonSx}
      >
        <VyIcon icon="Grid" style={{ transform: 'scale(0.8)' }} />
      </IconButton>

      <VyTableColumnSettings
        anchorEl={columnSettingsAnchorEl}
        open={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
        columns={columnSettings}
        handleColumnVisibilityChange={onColumnVisibilityChange}
        mandatoryColumnsCount={0}
      />
    </>
  );

  return (
    <RevenueReportToolbar
      hideDateRange
      hideChartToggle
      onSearchChange={onSearchChange}
      searchValue={searchValue}
      filterFields={filterFields}
      filters={filters}
      onFiltersChange={onFiltersChange}
      onExport={onExport}
      trailingActions={trailingActions}
    />
  );
};
